import { useState, useEffect } from "react";
import api, { formatApiError, customerAddressBook, formatAddressLine } from "@/lib/api";
import { toast } from "sonner";
import { Search, User, Phone, Trash2, X, Loader2, ShieldCheck, Pencil } from "lucide-react";

const EMPTY_FORM = {
  name: "",
  phone: "",
  email: "",
  address_id: "",
  label: "Home",
  is_default: true,
  line1: "",
  line2: "",
  city: "",
  state: "",
  pincode: "",
  landmark: "",
};

function fieldsFromAddress(a) {
  return {
    line1: a?.line1 || "",
    line2: a?.line2 || "",
    city: a?.city || "",
    state: a?.state || "",
    pincode: a?.pincode || "",
    landmark: a?.landmark || "",
    label: a?.label || "Home",
    is_default: Boolean(a?.is_default),
  };
}

function customerToForm(c, addressId) {
  const book = customerAddressBook(c);
  const chosen =
    addressId && addressId !== "new"
      ? book.find((a) => a.id === addressId) || book.find((a) => a.is_default) || book[0]
      : book.find((a) => a.is_default) || book[0];
  const slotId = addressId || chosen?.id || (book.length ? chosen?.id : "new");
  return {
    name: c?.name || "",
    phone: c?.phone || "",
    email: c?.email || "",
    address_id: slotId || "new",
    ...fieldsFromAddress(addressId === "new" ? { label: "Home", is_default: book.length === 0 } : chosen || c?.address),
  };
}

export default function Customers() {
  const [customers, setCustomers] = useState([]);
  const [q, setQ] = useState("");
  const [confirmDel, setConfirmDel] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const { data } = await api.get("/api/customers");
    setCustomers(data);
  };

  useEffect(() => {
    load();
  }, []);

  const openEdit = (c, addressId) => {
    setEditing(c);
    setForm(customerToForm(c, addressId));
  };

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const selectAddress = (id) => {
    const book = customerAddressBook(editing);
    if (id === "new") {
      setForm((prev) => ({
        ...prev,
        address_id: "new",
        ...fieldsFromAddress({ label: "Home", is_default: book.length === 0 }),
      }));
      return;
    }
    const chosen = book.find((a) => a.id === id);
    if (!chosen) return;
    setForm((prev) => ({
      ...prev,
      address_id: chosen.id,
      ...fieldsFromAddress(chosen),
    }));
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    if (!editing) return;
    const name = form.name.trim();
    const phone = form.phone.trim();
    if (!name) return toast.error("Name is required");
    if (!phone) return toast.error("Phone is required");

    const hasAddress = [form.line1, form.city, form.state, form.pincode].some((v) =>
      String(v || "").trim()
    );
    if (hasAddress) {
      if (!form.line1.trim() || !form.city.trim() || !form.state.trim() || !form.pincode.trim()) {
        return toast.error("Address needs line1, city, state, and pincode");
      }
    }

    setSaving(true);
    try {
      const body = {
        name,
        phone,
        email: form.email.trim(),
        address: hasAddress
          ? {
              line1: form.line1.trim(),
              line2: form.line2.trim(),
              city: form.city.trim(),
              state: form.state.trim(),
              pincode: form.pincode.trim(),
              landmark: form.landmark.trim(),
            }
          : null,
      };
      if (hasAddress && form.address_id) {
        body.address_id = form.address_id;
        body.address_label = form.label.trim() || "Home";
        body.address_is_default = Boolean(form.is_default);
      }
      await api.patch(`/api/customers/${editing.id}`, body);
      toast.success("Customer updated");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    if (!confirmDel) return;
    setDeleting(true);
    try {
      await api.delete(`/api/customers/${confirmDel.id}`);
      toast.success(`Erased ${confirmDel.name} — historical orders anonymized`);
      setConfirmDel(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setDeleting(false);
    }
  };

  const filtered = customers.filter((c) => {
    if (!q.trim()) return true;
    const t = q.toLowerCase();
    return (
      c.name?.toLowerCase().includes(t) ||
      c.phone?.includes(t) ||
      c.email?.toLowerCase().includes(t)
    );
  });

  return (
    <div className="p-4 sm:p-6 lg:p-12">
      <div className="mb-8">
        <div className="font-mono text-xs tracking-[0.3em] text-[color:var(--mecha-red)]">
          // CRM
        </div>
        <h1 className="font-display text-3xl sm:text-5xl mt-3 leading-none">CUSTOMERS</h1>
        <div className="text-zinc-500 text-sm mt-2 flex items-center gap-2">
          <ShieldCheck className="h-3.5 w-3.5" />
          DPDP-compliant: deleting a customer anonymizes them on all historical orders.
        </div>
      </div>

      <div className="relative mb-6">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
        <input
          data-testid="cust-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search name, phone, email..."
          className="w-full h-12 bg-zinc-950 border border-zinc-800 pl-12 pr-4 text-white placeholder-zinc-600 focus:outline-none focus:border-[color:var(--mecha-red)]"
        />
      </div>

      {filtered.length === 0 ? (
        <div className="border border-zinc-800 p-16 text-center">
          <User className="h-10 w-10 mx-auto text-zinc-600 mb-4" />
          <div className="text-zinc-400">No customers yet.</div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map((c) => (
            <div
              key={c.id}
              data-testid={`customer-card-${c.phone}`}
              className="card-brutal hoverable-card p-5"
            >
              <div className="flex items-start justify-between mb-3 gap-3">
                <div className="min-w-0">
                  <div className="text-lg font-medium truncate">{c.name}</div>
                  <div className="flex items-center gap-2 mt-1 text-zinc-400">
                    <Phone className="h-3 w-3" />
                    <span className="font-mono text-xs">{c.phone}</span>
                  </div>
                  {c.email && (
                    <div className="text-xs text-zinc-500 mt-0.5 truncate">{c.email}</div>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    data-testid={`cust-edit-${c.phone}`}
                    onClick={() => openEdit(c)}
                    title="Edit customer"
                    className="w-9 h-9 border border-zinc-800 hover:border-white hover:text-white flex items-center justify-center text-zinc-400"
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </button>
                  <button
                    data-testid={`cust-delete-${c.phone}`}
                    onClick={() => setConfirmDel(c)}
                    title="Erase customer (DPDP delete request)"
                    className="w-9 h-9 border border-zinc-800 hover:border-[color:var(--mecha-red)] hover:text-[color:var(--mecha-red)] flex items-center justify-center"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
              {customerAddressBook(c).length > 0 && (
                <div className="border-t border-zinc-800 pt-3">
                  <div className="font-mono text-[10px] tracking-[0.3em] text-zinc-500 mb-2">
                    {customerAddressBook(c).length > 1 ? "ADDRESSES" : "ADDRESS"}
                  </div>
                  <div className="space-y-2">
                    {customerAddressBook(c).map((a) => (
                      <div key={a.id} className="text-xs text-zinc-300 leading-relaxed flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <span className="font-mono text-[10px] tracking-widest text-zinc-500">
                            {a.label || "Home"}
                            {a.is_default ? " · DEFAULT" : ""}
                          </span>
                          <div>{formatAddressLine(a)}</div>
                        </div>
                        <button
                          type="button"
                          data-testid={`cust-edit-addr-${c.phone}-${a.id}`}
                          onClick={() => openEdit(c, a.id)}
                          title={`Edit ${a.label || "address"}`}
                          className="shrink-0 font-mono text-[10px] tracking-widest text-zinc-500 hover:text-white"
                        >
                          EDIT
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <form
            onSubmit={saveEdit}
            className="card-brutal w-full max-w-lg max-h-[90vh] overflow-y-auto"
          >
            <div className="flex items-center justify-between p-5 border-b border-zinc-800 sticky top-0 bg-zinc-950 z-10">
              <div>
                <div className="font-mono text-[10px] tracking-[0.3em] text-[color:var(--mecha-red)]">
                  // EDIT
                </div>
                <h2 className="font-display text-2xl mt-1">UPDATE CUSTOMER</h2>
              </div>
              <button
                type="button"
                onClick={() => setEditing(null)}
                className="text-zinc-500 hover:text-white"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-5 space-y-4">
              <Field
                label="Name"
                testId="cust-edit-name"
                value={form.name}
                onChange={(v) => setField("name", v)}
                required
              />
              <Field
                label="Phone"
                testId="cust-edit-phone"
                value={form.phone}
                onChange={(v) => setField("phone", v)}
                required
              />
              <Field
                label="Email"
                testId="cust-edit-email"
                value={form.email}
                onChange={(v) => setField("email", v)}
                type="email"
              />

              <div className="font-mono text-[10px] tracking-[0.3em] text-zinc-500 pt-2">
                ADDRESS
              </div>
              {customerAddressBook(editing).length > 0 && (
                <div className="space-y-2" data-testid="cust-edit-address-picker">
                  <div className="text-xs text-zinc-400">
                    Choose which saved address to edit, or add a new one.
                  </div>
                  {customerAddressBook(editing).map((a) => (
                    <label
                      key={a.id}
                      className={`block p-3 border cursor-pointer ${
                        form.address_id === a.id
                          ? "border-[color:var(--neon-cyan)]"
                          : "border-zinc-800"
                      }`}
                    >
                      <input
                        type="radio"
                        name="cust-edit-address"
                        className="mr-2"
                        checked={form.address_id === a.id}
                        onChange={() => selectAddress(a.id)}
                      />
                      <span className="text-sm font-medium">
                        {a.label || "Address"}
                        {a.is_default ? " · default" : ""}
                      </span>
                      <div className="text-xs text-zinc-400 mt-1 pl-6">{formatAddressLine(a)}</div>
                    </label>
                  ))}
                  <label
                    className={`block p-3 border cursor-pointer ${
                      form.address_id === "new"
                        ? "border-[color:var(--neon-cyan)]"
                        : "border-zinc-800"
                    }`}
                  >
                    <input
                      type="radio"
                      name="cust-edit-address"
                      className="mr-2"
                      checked={form.address_id === "new"}
                      onChange={() => selectAddress("new")}
                    />
                    <span className="text-sm font-medium">Add new address</span>
                  </label>
                </div>
              )}
              <Field
                label="Label"
                testId="cust-edit-label"
                value={form.label}
                onChange={(v) => setField("label", v)}
              />
              <Field
                label="Line 1"
                testId="cust-edit-line1"
                value={form.line1}
                onChange={(v) => setField("line1", v)}
              />
              <Field
                label="Line 2"
                testId="cust-edit-line2"
                value={form.line2}
                onChange={(v) => setField("line2", v)}
              />
              <div className="grid grid-cols-2 gap-3">
                <Field
                  label="City"
                  testId="cust-edit-city"
                  value={form.city}
                  onChange={(v) => setField("city", v)}
                />
                <Field
                  label="State"
                  testId="cust-edit-state"
                  value={form.state}
                  onChange={(v) => setField("state", v)}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field
                  label="Pincode"
                  testId="cust-edit-pincode"
                  value={form.pincode}
                  onChange={(v) => setField("pincode", v)}
                />
                <Field
                  label="Landmark"
                  testId="cust-edit-landmark"
                  value={form.landmark}
                  onChange={(v) => setField("landmark", v)}
                />
              </div>
              <label className="flex items-start gap-3 text-sm text-zinc-300 cursor-pointer">
                <input
                  type="checkbox"
                  data-testid="cust-edit-default"
                  checked={Boolean(form.is_default)}
                  onChange={(e) => setField("is_default", e.target.checked)}
                  className="mt-1"
                />
                <span>Default shipping address</span>
              </label>

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setEditing(null)}
                  className="flex-1 h-11 border border-zinc-800 hover:border-white text-xs font-mono tracking-widest uppercase"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  data-testid="cust-edit-save"
                  disabled={saving}
                  className="btn-brutal flex-1 h-11 bg-[color:var(--mecha-red)] text-white text-xs font-mono tracking-widest uppercase disabled:opacity-60 flex items-center justify-center gap-2"
                >
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save changes"}
                </button>
              </div>
            </div>
          </form>
        </div>
      )}

      {confirmDel && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="card-brutal w-full max-w-md">
            <div className="flex items-center justify-between p-5 border-b border-zinc-800">
              <div>
                <div className="font-mono text-[10px] tracking-[0.3em] text-[color:var(--mecha-red)]">
                  // ERASE
                </div>
                <h2 className="font-display text-2xl mt-1">DELETE CUSTOMER</h2>
              </div>
              <button
                onClick={() => setConfirmDel(null)}
                className="text-zinc-500 hover:text-white"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div className="text-sm text-zinc-300 leading-relaxed">
                This will permanently delete <strong className="text-white">{confirmDel.name}</strong> ({confirmDel.phone}) and anonymize them on every past order. This cannot be undone.
              </div>
              <div className="border border-[color:var(--warn)] bg-[color:var(--warn)]/5 p-3 text-xs text-[color:var(--warn)]">
                Use this to honour DPDP / GDPR delete requests from the customer.
              </div>
              <div className="flex gap-3 pt-1">
                <button
                  type="button"
                  onClick={() => setConfirmDel(null)}
                  className="flex-1 h-11 border border-zinc-800 hover:border-white text-xs font-mono tracking-widest uppercase"
                >
                  Cancel
                </button>
                <button
                  data-testid="cust-delete-confirm"
                  onClick={doDelete}
                  disabled={deleting}
                  className="btn-brutal flex-1 h-11 bg-[color:var(--mecha-red)] text-white text-xs font-mono tracking-widest uppercase disabled:opacity-60 flex items-center justify-center gap-2"
                >
                  {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Erase Permanently"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, value, onChange, testId, type = "text", required = false }) {
  return (
    <label className="block">
      <div className="font-mono text-[10px] tracking-[0.3em] text-zinc-500 mb-1.5 uppercase">
        {label}
        {required ? " *" : ""}
      </div>
      <input
        data-testid={testId}
        type={type}
        value={value}
        required={required}
        onChange={(e) => onChange(e.target.value)}
        className="w-full h-11 bg-zinc-950 border border-zinc-800 px-3 text-sm text-white focus:outline-none focus:border-[color:var(--mecha-red)]"
      />
    </label>
  );
}
